import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Camera, Upload, CheckCircle2, XCircle, Loader2,
  Package, Wrench, AlertCircle, ImageIcon, ChevronRight, Mic, Square, FolderOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice Input Hook
const useVoiceInput = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-IN';

      recognitionRef.current.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }
    return () => recognitionRef.current?.abort();
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  return { isListening, transcript, startListening, stopListening, setTranscript };
};

const WorkInProgress = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [approvedParts, setApprovedParts] = useState([]);
  const [approvedLabour, setApprovedLabour] = useState([]);
  const [wipProofs, setWipProofs] = useState([]);
  const [wipLabourDone, setWipLabourDone] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, percentage: 0 });
  
  const [currentItemId, setCurrentItemId] = useState(null);
  const [description, setDescription] = useState('');
  const [labourEditId, setLabourEditId] = useState(null);
  const [labourNotes, setLabourNotes] = useState('');
  const [labourSaving, setLabourSaving] = useState(false);
  
  const voiceInput = useVoiceInput();

  useEffect(() => {
    fetchWipStatus();
  }, [jobId]);

  useEffect(() => {
    if (voiceInput.transcript && !voiceInput.isListening) {
      setDescription(prev => prev ? `${prev} ${voiceInput.transcript}` : voiceInput.transcript);
      voiceInput.setTranscript('');
    }
  }, [voiceInput.transcript, voiceInput.isListening]);

  const fetchWipStatus = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/wip/${jobId}/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setJob(data);
        // Combine approved parts and body shop items (these need photos)
        const photoItems = [
          ...(data.approved_parts || []),
          ...(data.approved_body_shop || [])
        ];
        setApprovedParts(photoItems);
        setApprovedLabour(data.approved_labour || []);
        setWipProofs(data.wip_proofs || []);
        setWipLabourDone(data.wip_labour_done || []);
        setProgress(data.progress || { total: 0, completed: 0, percentage: 0 });
      }
    } catch (error) {
      toast.error('Failed to load work in progress status');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    console.log('File selected:', file?.name, file?.size);
    
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    if (!currentItemId) {
      toast.error('Please select an item first');
      return;
    }

    if (!description.trim()) {
      toast.error('Please describe what was installed/done');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('garage_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('estimate_item_id', currentItemId);
    formData.append('description', description);

    console.log('Uploading to:', `${API_URL}/api/garage/wip/${jobId}/proof`);
    console.log('Item ID:', currentItemId);
    console.log('Description:', description);

    try {
      const response = await fetch(`${API_URL}/api/garage/wip/${jobId}/proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          toast.error(errorData.detail || `Upload failed (${response.status})`);
        } catch {
          toast.error(`Upload failed: ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      console.log('Upload response:', data);
      
      if (data.success) {
        toast.success('New part photo uploaded successfully!');
        setDescription('');
        setCurrentItemId(null);
        fetchWipStatus();
        
        if (data.can_complete_work) {
          toast.success('All parts have photos! You can complete the work.');
        }
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload exception:', error);
      toast.error('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleCompleteWip = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/wip/${jobId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        // Navigate to job detail page (QC stage removed - go directly to invoice generation)
        navigate(`/garage/jobs/${jobId}`);
      } else {
        toast.error(data.detail || 'Cannot complete work');
      }
    } catch (error) {
      toast.error('Failed to complete work');
    }
  };

  const getPartWipProof = (partId) => {
    return wipProofs.find(p => p.estimate_item_id === partId);
  };

  const getLabourDone = (itemId) => {
    return wipLabourDone.find(l => l.estimate_item_id === itemId);
  };

  const handleMarkLabourDone = async (itemId) => {
    const token = localStorage.getItem('garage_token');
    setLabourSaving(true);
    try {
      const formData = new FormData();
      formData.append('estimate_item_id', itemId);
      formData.append('notes', labourNotes || '');
      const response = await fetch(`${API_URL}/api/garage/wip/${jobId}/labour/done`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Labour marked as done');
        setLabourEditId(null);
        setLabourNotes('');
        fetchWipStatus();
      } else {
        toast.error(data.detail || 'Failed to mark as done');
      }
    } catch (error) {
      toast.error('Failed to mark as done: ' + error.message);
    } finally {
      setLabourSaving(false);
    }
  };

  const handleUnmarkLabour = async (itemId) => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/wip/${jobId}/labour/${itemId}/done`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Reverted to pending');
        fetchWipStatus();
      } else {
        toast.error('Failed to revert');
      }
    } catch {
      toast.error('Failed to revert');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="wip-page" className={`min-h-screen ${theme.pageBg} transition-colors duration-200`}>
      {/* Hidden file inputs - one for camera, one for gallery */}
      <input
        type="file"
        ref={cameraInputRef}
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handleUploadPhoto}
        data-testid="wip-camera-input"
      />
      <input
        type="file"
        ref={galleryInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleUploadPhoto}
        data-testid="wip-gallery-input"
      />

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/garage/jobs/${jobId}`)}
              className={theme.textSecondary}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Work In Progress</h1>
              <p className={`text-sm ${theme.textSecondary}`}>{job?.job_number}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-orange-500 border-orange-500">
            {job?.status?.replace(/_/g, ' ').toUpperCase()}
          </Badge>
        </div>

        {/* Customer Complaint */}
        {job?.customer_complaint && (
          <Card className={`${theme.infoBg} border`}>
            <CardContent className="p-4">
              <p className={`text-sm font-medium ${theme.infoText} mb-1`}>Customer Complaint:</p>
              <p className={theme.textPrimary}>{job.customer_complaint}</p>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card className={`${theme.cardBg} border`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className={theme.textSecondary}>Work Progress</span>
              <span className={`font-bold ${theme.textPrimary}`}>
                {progress.completed}/{progress.total} items
              </span>
            </div>
            <Progress value={progress.percentage} className="h-2" />
            <p className={`text-xs ${theme.textMuted} mt-2`}>
              Photos for parts/body shop · Mark labour as done
            </p>
          </CardContent>
        </Card>

        {/* Items Checklist */}
        <div className="space-y-4">
          <h2 className={`${theme.textPrimary} font-semibold`}>Approved Items Checklist</h2>
          
          {approvedParts.length === 0 && approvedLabour.length === 0 ? (
            <Card className={`${theme.cardBg} border`}>
              <CardContent className="p-8 text-center">
                <Package className={`w-12 h-12 mx-auto mb-4 ${theme.textMuted}`} />
                <p className={theme.textSecondary}>No approved items found</p>
              </CardContent>
            </Card>
          ) : (
            <>
            {approvedParts.map((part) => {
              const proof = getPartWipProof(part.id);
              const hasProof = !!proof;
              const isBodyShop = part.item_type === 'body_shop_labour';
              
              return (
                <Card 
                  key={part.id} 
                  className={`${hasProof ? theme.successBg : theme.cardBg} border transition-all ${
                    isBodyShop ? 'border-l-4 border-l-purple-500' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {hasProof ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : isBodyShop ? (
                          <Wrench className={`w-6 h-6 text-purple-400`} />
                        ) : (
                          <Package className={`w-6 h-6 ${theme.textMuted}`} />
                        )}
                        <div>
                          <h3 className={`font-medium ${theme.textPrimary}`}>
                            {part.part_name || part.description}
                          </h3>
                          <p className={`text-sm ${theme.textSecondary}`}>{part.description}</p>
                          {isBodyShop && (
                            <Badge className="mt-1 bg-purple-600 text-xs">Body Shop Work</Badge>
                          )}
                        </div>
                      </div>
                      <Badge className={hasProof ? 'bg-green-500' : 'bg-slate-500'}>
                        {hasProof ? 'Uploaded' : 'Pending'}
                      </Badge>
                    </div>

                    {/* Show damage photos for body shop items */}
                    {isBodyShop && part.damage_images?.length > 0 && (
                      <div className="mb-3">
                        <p className={`text-xs ${theme.textMuted} mb-2`}>Damage Photos (Before):</p>
                        <div className="grid grid-cols-4 gap-2">
                          {part.damage_images.map((img, idx) => {
                            const imgUrl = img.startsWith('/uploads/') ? `${API_URL}/api${img}` : `${API_URL}${img}`;
                            return (
                              <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-purple-500/50">
                                <img src={imgUrl} alt="Damage" className="w-full h-full object-cover" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Show existing proof images */}
                    {proof?.new_part_images?.length > 0 && (
                      <div className="mb-3">
                        <p className={`text-xs ${theme.textMuted} mb-2`}>
                          {isBodyShop ? 'Work Completion Photos:' : 'New Part Photos:'}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {proof.new_part_images.map((img, idx) => {
                            const imgUrl = img.startsWith('/uploads/') ? `${API_URL}/api${img}` : `${API_URL}${img}`;
                            return (
                              <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-slate-600">
                                <img src={imgUrl} alt="Work done" className="w-full h-full object-cover" />
                              </div>
                            );
                          })}
                        </div>
                        <p className={`text-sm ${theme.textSecondary} mt-2`}>
                          {proof.description}
                        </p>
                      </div>
                    )}

                    {/* Upload form */}
                    {currentItemId === part.id ? (
                      <div className="space-y-3 mt-4 p-3 bg-slate-800/30 rounded-lg">
                        <div className="flex gap-2">
                          <Textarea
                            placeholder={isBodyShop 
                              ? "Describe the completed body work (e.g., 'Dent removed, panel smoothed and primed')"
                              : "Describe what was installed (e.g., 'Installed new OEM brake pads')"
                            }
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={`${theme.inputBg} ${theme.textPrimary} flex-1`}
                            rows={2}
                          />
                          <Button
                            variant={voiceInput.isListening ? "destructive" : "outline"}
                            size="icon"
                            onClick={voiceInput.isListening ? voiceInput.stopListening : voiceInput.startListening}
                            className={`flex-shrink-0 h-auto ${voiceInput.isListening ? 'animate-pulse' : 'border-orange-500 text-orange-500'}`}
                          >
                            {voiceInput.isListening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            data-testid="wip-take-photo-btn"
                            onClick={() => cameraInputRef.current?.click()}
                            disabled={uploading || !description.trim()}
                            className={isBodyShop ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"}
                          >
                            {uploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Camera className="w-4 h-4 mr-2" />
                            )}
                            Take Photo
                          </Button>
                          <Button
                            data-testid="wip-from-gallery-btn"
                            onClick={() => galleryInputRef.current?.click()}
                            disabled={uploading || !description.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {uploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <FolderOpen className="w-4 h-4 mr-2" />
                            )}
                            From Gallery
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => { setCurrentItemId(null); setDescription(''); }}
                          className={`w-full ${theme.border} ${theme.textSecondary}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={() => setCurrentItemId(part.id)}
                          variant={hasProof ? "outline" : "default"}
                          className={`flex-1 ${hasProof 
                            ? `${theme.border} ${theme.textSecondary}` 
                            : isBodyShop 
                              ? 'bg-purple-600 hover:bg-purple-700' 
                              : 'bg-orange-600 hover:bg-orange-700'
                          }`}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          {hasProof ? 'Add More Photos' : isBodyShop ? 'Upload Completion Photo' : 'Upload Photo'}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Labour items checklist (no photo required) */}
            {approvedLabour.map((labour) => {
              const done = getLabourDone(labour.id);
              const isDone = !!done;
              const isEditing = labourEditId === labour.id;
              return (
                <Card
                  key={labour.id}
                  data-testid={`wip-labour-card-${labour.id}`}
                  className={`${isDone ? theme.successBg : theme.cardBg} border transition-all border-l-4 border-l-orange-400`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {isDone ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Wrench className="w-6 h-6 text-orange-400" />
                        )}
                        <div>
                          <h3 className={`font-medium ${theme.textPrimary}`}>
                            {labour.description || labour.part_name || 'Labour'}
                          </h3>
                          <Badge className="mt-1 bg-orange-600 text-xs">Labour · No photo needed</Badge>
                        </div>
                      </div>
                      <Badge className={isDone ? 'bg-green-500' : 'bg-slate-500'}>
                        {isDone ? 'Done' : 'Pending'}
                      </Badge>
                    </div>

                    {isDone && done?.notes && (
                      <p className={`text-sm ${theme.textSecondary} mb-3 italic`}>
                        Notes: {done.notes}
                      </p>
                    )}

                    {isEditing ? (
                      <div className="space-y-3 mt-2 p-3 bg-slate-800/30 rounded-lg">
                        <Textarea
                          placeholder="Optional notes (e.g., 'Brake fluid flushed and bled all 4 wheels')"
                          value={labourNotes}
                          onChange={(e) => setLabourNotes(e.target.value)}
                          className={`${theme.inputBg} ${theme.textPrimary}`}
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            data-testid={`wip-labour-confirm-${labour.id}`}
                            onClick={() => handleMarkLabourDone(labour.id)}
                            disabled={labourSaving}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {labourSaving ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            Confirm Done
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setLabourEditId(null); setLabourNotes(''); }}
                            className={`${theme.border} ${theme.textSecondary}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-2">
                        {isDone ? (
                          <Button
                            data-testid={`wip-labour-undo-${labour.id}`}
                            onClick={() => handleUnmarkLabour(labour.id)}
                            variant="outline"
                            className={`flex-1 ${theme.border} ${theme.textSecondary}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Mark as Pending
                          </Button>
                        ) : (
                          <Button
                            data-testid={`wip-labour-mark-${labour.id}`}
                            onClick={() => { setLabourEditId(labour.id); setLabourNotes(''); }}
                            className="flex-1 bg-orange-600 hover:bg-orange-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Mark as Done
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            </>
          )}
        </div>

        {/* Complete Button */}
        {progress.percentage === 100 && (
          <Button
            onClick={handleCompleteWip}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 h-14 text-lg"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Work Complete - Ready for Invoice
          </Button>
        )}

        {progress.percentage < 100 && (
          <p className={`text-center ${theme.textMuted} text-sm`}>
            Complete each item — upload photos for parts/body shop, mark labour as done
          </p>
        )}
      </main>
    </div>
  );
};

export default WorkInProgress;
